import React, { useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import VectorIcon from 'assets/actionPlan/vector.svg';
import Button from 'common-ui/button';
import CustomDatePicker from 'common-ui/customDatePicker';
import Input from 'common-ui/input';
import Modal from 'common-ui/modal';
import Switch from 'common-ui/switch';
import TextArea from 'common-ui/textArea';
import dayjs from 'dayjs';

import { createAction, getActions } from '../../../features/actions/actionPlanActions';
import { selectCreateLoading } from '../../../features/actions/actionPlanSlice';
import { actionSchema } from '../validationSchemas';
import {
  AlertIcon,
  ButtonWrapper,
  ErrorForInputs,
  ErrorMessage,
  FormWrapper,
  SwitchWrapper,
} from './CreateActionModal.styles';

const CreateActionModal = ({
  isOpen,
  onClose,
  isEffectiveDate = false,
  onConfirm,
  onSuccess,
  earliestAssignmentDate = null,
}) => {
  const dispatch = useDispatch();
  const isCreating = useSelector(selectCreateLoading);
  const [isEnabled, setIsEnabled] = useState(true);
  const [actionName, setActionName] = useState('');
  const [description, setDescription] = useState('');
  const [date, setDate] = useState(null);
  const [errors, setErrors] = useState({});
  const tomorrow = dayjs().add(1, 'day').startOf('day');

  const minDate =
    isEffectiveDate && earliestAssignmentDate
      ? dayjs(earliestAssignmentDate).add(1, 'day').startOf('day')
      : tomorrow;

  const handleSubmit = async () => {
    setErrors({});

    if (isEffectiveDate) {
      if (!date) {
        setErrors({ effectiveDate: 'This field is required' });
        return;
      }

      try {
        const parsedDate = dayjs(date);
        const validationDate = parsedDate.startOf('day').toDate();

        await actionSchema.validateAt('effectiveDate', {
          effectiveDate: validationDate,
        });

        if (onConfirm) {
          await onConfirm(parsedDate.format('YYYY-MM-DD'));
        }

        handleClose();
      } catch (err) {
        setErrors({ effectiveDate: err.message || 'Invalid date' });
      }
      return;
    }

    try {
      const tomorrowStr = dayjs().add(1, 'day').format('YYYY-MM-DD');
      const finalDate = date ? dayjs(date).format('YYYY-MM-DD') : tomorrowStr;

      await actionSchema.validate(
        {
          actionName,
          description,
          effectiveDate: dayjs(finalDate).startOf('day').toDate(),
        },
        { abortEarly: false }
      );

      const actionData = {
        actionName,
        description,
        enabled: isEnabled,
        effectiveDate: finalDate,
      };

      const resultAction = await dispatch(createAction(actionData));

      if (createAction.fulfilled.match(resultAction)) {
        if (onSuccess) {
          onSuccess();
        } else {
          dispatch(getActions());
        }
        handleClose();
      } else {
        setErrors({
          actionName: resultAction.payload || 'Server error',
        });
      }
    } catch (err) {
      const validationErrors = {};
      if (err.inner && Array.isArray(err.inner)) {
        err.inner.forEach((error) => {
          if (error.path) validationErrors[error.path] = error.message;
        });
      } else if (err.path) {
        validationErrors[err.path] = err.message;
      }
      setErrors(validationErrors);
    }
  };
  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleDateChange = (newValue) => {
    setDate(newValue);

    if (errors.effectiveDate) {
      setErrors((prev) => {
        const { effectiveDate, ...rest } = prev;
        return rest;
      });
    }
  };

  const resetForm = () => {
    setActionName('');
    setDescription('');
    setDate(null);
    setIsEnabled(true);
    setErrors({});
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEffectiveDate ? 'Effective date' : 'Create New Action'}
      width="448px"
      $centered
      footer={false}
    >
      <FormWrapper>
        {isEffectiveDate ? (
          <div>
            <CustomDatePicker
              label="Effective date"
              required
              placeholder="Select a date"
              value={date}
              onChange={handleDateChange}
              error={!!errors.effectiveDate}
              minDate={minDate}
              slotProps={{
                actionBar: {
                  actions: [],
                },
              }}
            />

            {errors.effectiveDate && (
              <ErrorForInputs>
                <AlertIcon src={VectorIcon} />
                <ErrorMessage>{errors.effectiveDate}</ErrorMessage>
              </ErrorForInputs>
            )}
          </div>
        ) : (
          <>
            <Input
              label="Action Name"
              required
              placeholder="Enter action name"
              value={actionName}
              onChange={(e) => {
                setActionName(e.target.value);
                setErrors({});
              }}
              error={!!errors.actionName}
              fullWidth
            />
            {errors.actionName && (
              <ErrorForInputs>
                <AlertIcon src={VectorIcon} />
                <ErrorMessage>{errors.actionName}</ErrorMessage>
              </ErrorForInputs>
            )}
            <TextArea
              label="Description"
              tooltip="Expand action steps"
              placeholder="Add description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={250}
              resizeHorizontal={false}
            />
            <SwitchWrapper $isEnabled={isEnabled}>
              <Switch isOn={isEnabled} onToggle={() => setIsEnabled(!isEnabled)} />
              {isEnabled ? 'Enabled' : 'Disabled'}
            </SwitchWrapper>
          </>
        )}

        <ButtonWrapper>
          <Button outlined onClick={handleClose} width="45%">
            Cancel
          </Button>
          <Button
            primary
            onClick={handleSubmit}
            width="50%"
            loading={isEffectiveDate ? false : isCreating}
          >
            {isEffectiveDate ? 'Submit' : 'Create Action'}
          </Button>
        </ButtonWrapper>
      </FormWrapper>
    </Modal>
  );
};

export default CreateActionModal;
